/**
 * Billing API endpoints for Dodo Payments integration
 */

import { getRequestWithAuth, postRequestWithAuth } from "~/lib/api";

export interface UsageSummary {
  user_id: string;
  plan: "free" | "basic" | "pro" | "max";
  monthly_coin_limit: number | null; // Remaining gamgeeCoins (decrements on usage)
  within_limit: boolean;
  dodo_customer_id: string | null;
  payment_method_configured: boolean;
}

export interface UpgradeResponse {
  success: boolean;
  message: string;
  plan: string;
  payment_link: string | null;
}

export interface BillingProfile {
  user_id: string;
  billing_plan: string;
  dodo_customer_id: string | null;
  monthly_coin_limit: number | null;
  payment_method_configured: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get current user's usage summary
 */
export const getUsageSummary = async (): Promise<UsageSummary> => {
  return await getRequestWithAuth("/api/v1/billing/usage");
};

/**
 * Upgrade user's billing plan
 */
export const upgradePlan = async (
  plan: "free" | "basic" | "pro" | "max",
  theme?: "dark" | "light"
): Promise<UpgradeResponse> => {
  return await postRequestWithAuth("/api/v1/billing/upgrade", {
    plan,
    theme: theme || "dark",
  });
};

/**
 * Get user's billing profile
 */
export const getBillingProfile = async (): Promise<BillingProfile> => {
  return await getRequestWithAuth("/api/v1/billing/profile");
};

export interface CustomerPortalResponse {
  success: boolean;
  portal_url: string;
  message: string;
}

export interface ProrationDetails {
  current_plan: string;
  new_plan: string;
  new_plan_price: number;
  adjustment: number;
  subtotal: number;
  tax: number;
  total: number;
  renewal_date: string;
}

export interface SubscriptionDetails {
  status: string;
  billing_plan?: string;
  next_billing_date: string | null;
  previous_billing_date: string | null;
  cancel_at_next_billing_date: boolean;
  created_at: string | null;
  recurring_pre_tax_amount: number | null;
  currency: string;
  payment_frequency_interval: string | null;
  payment_frequency_count: number | null;
  expires_at: string | null;
  cancelled_at: string | null;
  message?: string;
  proration?: ProrationDetails;
}

/**
 * Get current user's subscription details with proration calculation
 * @param targetPlan Target plan for proration calculation
 */
export const getSubscriptionDetails = async (
  targetPlan: "basic" | "pro" | "max"
): Promise<SubscriptionDetails> => {
  return await getRequestWithAuth(
    `/api/v1/billing/subscription?target_plan=${targetPlan}`
  );
};

/**
 * Create customer portal session for billing management
 */
export const createCustomerPortalSession =
  async (): Promise<CustomerPortalResponse> => {
    return await postRequestWithAuth(
      "/api/v1/billing/manage-customer-billing",
      {}
    );
  };

export interface CheckoutStatus {
  session_id: string;
  payment_status: "pending" | "succeeded" | "failed" | "expired";
  customer_id: string;
  subscription_id?: string;
  amount_total: number;
  currency: string;
  created_at: string;
  expires_at: string;
}

/**
 * Get latest checkout session status for current user
 */
export const getCheckoutStatus = async (): Promise<CheckoutStatus> => {
  return await getRequestWithAuth("/api/v1/billing/checkout-status");
};

export interface ContactSalesRequest {
  email: string;
  name?: string;
  company?: string;
  message?: string;
}

export interface ContactSalesResponse {
  success: boolean;
  message: string;
}

/**
 * Submit contact sales request
 */
export const contactSales = async (
  data: ContactSalesRequest
): Promise<ContactSalesResponse> => {
  return await postRequestWithAuth("/api/v1/billing/contact-sales", data);
};
